import { useState } from 'react'

import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  Tooltip,
} from '@buffer-mono/popcorn'

import { DEFAULT_STATUS, STATUS_LABELS, type FilterStatus } from './types'
import { useDelayedHover } from '../hooks/useDelayedHover'

type FilterStatusMenuProps = {
  value?: FilterStatus
  onChange?: (value: FilterStatus) => void
}

const isValidStatus = (newValue: string): newValue is FilterStatus => {
  return Object.keys(STATUS_LABELS).includes(newValue)
}

export const FilterStatusMenu = ({
  value = DEFAULT_STATUS,
  onChange,
}: FilterStatusMenuProps): JSX.Element => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  const { handleMouseEnter, handleMouseLeave } = useDelayedHover({
    onOpen: () => setShowTooltip(true),
    onClose: () => setShowTooltip(false),
    openDelayDuration: 1000,
    closeDelayDuration: 100, // How long to wait before hiding after mouse leave
    isOpen: showTooltip,
  })

  const handleStatusChange = (newValue: string): void => {
    if (!newValue || newValue === value || !isValidStatus(newValue)) return

    onChange?.(newValue)
  }

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <Tooltip
        content="Filter by comment status"
        open={showTooltip && !isDropdownOpen}
      >
        <DropdownMenu.Trigger asChild>
          <Button
            data-testid="status-filter-button"
            aria-label="Filter by status"
            size="medium"
            variant="tertiary"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {STATUS_LABELS[value]}
            <ChevronDownIcon />
          </Button>
        </DropdownMenu.Trigger>
      </Tooltip>

      <DropdownMenu.Content align="end" data-no-focus-lock>
        <DropdownMenu.RadioGroup value={value} onChange={handleStatusChange}>
          <DropdownMenu.RadioItem value="unreplied">
            {STATUS_LABELS.unreplied}
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="replied">
            {STATUS_LABELS.replied}
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="dismissed">
            {STATUS_LABELS.dismissed}
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="all">
            {STATUS_LABELS.all}
          </DropdownMenu.RadioItem>
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
